import { isExpTenant } from '@tenants';
import AspectRatio from 'components/aspect-ratio';
import { ComingSoonBadge, ListingBadge } from 'ui/badges';
import { capitalizeWords } from '@zoocasa/node-kit/strings/capitalize';
import ConditionalWrapper from 'components/conditional-wrapper';
import Link from 'components/link';
import ListingCardNewOverlay from 'components/listing-card-new-overlay';
import FavouriteToggle from 'components/listing-card/favourite-toggle';
import ListingBedBathSqftLabels from 'components/listing-card/listing-bed-bath-sqft-labels';
import ListingImage from 'components/listing-image';
import ListingPrice from 'components/listing-price';
import ListingStatus, { LISTING_STATUS_SIZES } from 'components/listing-status';
import ListingTimestamp from 'components/listing-timestamp';
import SchemaOrg from 'components/schema-org';
import { locationsToNotUseListingModals } from 'constants/no-listing-modal-locations';
import { listingCardIds } from 'constants/test-constants';
import { useThemeContext, useUserContext } from 'contexts';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useIsMobile } from 'hooks/use-size-class';
import useWindowWidth from 'hooks/use-window-width';
import { FileText, Lock } from 'lucide-react';
import { MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { buildClassName } from 'utils/build-class-name';
import generateFullAddress from 'utils/get-full-address';
import getImageSrc from 'utils/get-image';
import { trackEvent } from 'utils/google-tag-manager';
import getListingImageAlt from 'utils/image-alt-description';
import { isListingVisibleToUser } from 'utils/compliance';
import { ProvinceOrStateCode, isUsStateCode } from 'utils/province_or_state';
import ProviderLabels from './provider-labels';
import ProviderLogo from './provider-logo';
import styles from './listing-card-v3.module.scss';
import { Provider, StandardStatus } from '@zoocasa/go-search';
import { isActive, isComingSoon } from 'types/reso';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import type { ListingCardProps } from './listing-card';
import { PROPERTY_FAVOURITED_SOURCE_AREA_PAGE, PROPERTY_FAVOURITED_SOURCE_MAP_GRID } from '@bedrock/analytics';

export type ListingCardV3Props = ListingCardProps & {
  /**
   * Smart tags currently being filtered for. When non-empty, the card surfaces
   * the listing's own tags that match these (up to {@link MAX_MATCHED_SMART_TAGS}),
   * so the user can see why a boosted listing surfaced.
   */
  activeSmartTagNames?: string[];
  /**
   * When no smart tags are being filtered for, surface the listing's first own
   * tag instead of none. Ignored while a filter is active, so a listing that
   * matches none of the filtered tags still shows no chip.
   */
  showDefaultSmartTag?: boolean;
};

/** Cap matched-tag chips so they never crowd the image or the status badges. */
const MAX_MATCHED_SMART_TAGS = 1;

function GatedMediaPill({ isTerms }: { isTerms: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const label = isTerms
    ? t(LOCALE_COMMON_PATHS.LISTING_CARD_VIEW_TERMS_OF_USE_OVERLAY_TEXT, { defaultValue: 'View Terms of Use' })
    : t(LOCALE_COMMON_PATHS.LISTING_CARD_SIGN_IN_OVERLAY_TEXT, { defaultValue: 'Sign In Required' });
  const Icon = isTerms ? FileText : Lock;

  return (
    <span className={styles.gatedPill} data-testid="listing-card-v3-gated-pill">
      <Icon size={15} aria-hidden />
      {label}
    </span>
  );
}

/**
 * Area Page listing card visual (v3). Keeps the legacy ListingCardData / click
 * contract; does not replace compliance {@link listing-card-v2}.
 */
export default function ListingCardV3({
  listing,
  listingToCompare,
  isWidget = false,
  isImageVisible,
  isCrawler,
  imageDescFallback = getListingImageAlt,
  pvrSentDate,
  gtmOnClickEvent,
  isOnMapSidePanel = false,
  onListingCardClick,
  favouriteEventTracker,
  favouriteSource,
  activeSmartTagNames,
  showDefaultSmartTag = false,
}: ListingCardV3Props) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { tenantId } = useThemeContext();
  const useExpStyle = isExpTenant(tenantId);
  const {
    id: listingId,
    imageUrl,
    imageDesc = '',
    isImageReady,
    addressPathWithFallback,
    isCrea,
    isPillar9Vow,
    city,
    province,
    isPrivate: listingIsPrivate,
    position,
    getStreet,
    providerId,
    isSignInAndTermsOfUseRequired,
    displayAddress = true,
    standardStatus,
  } = listing;
  const favouriteListingId = Number(listingId);
  const favouriteAddressSlug = listing.slug?.trim() || undefined;
  const { isAuthenticated, acceptedBrowserBasedTerms, acceptedTerms } = useUserContext();
  const { formatDateWithTemplate } = useI18nFormatting();
  const windowWidth = useWindowWidth();
  const listingCardElement = useRef<HTMLDivElement>(null);
  const [isSmallCard, setIsSmallCard] = useState(true);
  const isPrivate = !isAuthenticated && listingIsPrivate;
  const imageAlt = imageDesc && imageDesc.length > 0 ? imageDesc : imageDescFallback(listing);
  const street = getStreet(isAuthenticated);
  const showsListing = providerId ? isListingVisibleToUser(isPrivate, providerId, acceptedBrowserBasedTerms, acceptedTerms) : false;
  const shouldScramble = isPillar9Vow && !showsListing;
  const isNoModalLocation = province !== null && locationsToNotUseListingModals.includes(province);
  const isMobile = useIsMobile();
  const noModal = isNoModalLocation || isMobile;
  const showFavouriteToggle = showsListing && !isWidget;
  const isHidden = isPrivate || (isSignInAndTermsOfUseRequired && !showsListing && !isAuthenticated);
  const isTermsRequired = !showsListing && (isCrea || isSignInAndTermsOfUseRequired);
  const showGatedBody = isHidden || (isTermsRequired && isAuthenticated) || (isTermsRequired && isCrea);
  const showTermsPill = Boolean(isTermsRequired && !(isHidden && !isCrea));
  const shouldHideSquareFootage = providerId === Provider.PROVIDER_TRIAD_MULTIPLE_LISTING_SERVICE && standardStatus === StandardStatus.STANDARD_STATUS_ACTIVE;

  const matchedSmartTags = useMemo(() => {
    if (!listing.smartTags?.length) {
      return [];
    }
    if (!activeSmartTagNames?.length) {
      return showDefaultSmartTag ? listing.smartTags.slice(0, MAX_MATCHED_SMART_TAGS) : [];
    }
    const active = new Set(activeSmartTagNames.map(name => name.toLowerCase()));
    return listing.smartTags
      .filter(tag => active.has(tag.toLowerCase()))
      .slice(0, MAX_MATCHED_SMART_TAGS);
  }, [activeSmartTagNames, listing.smartTags, showDefaultSmartTag]);

  const analyticsProps = useMemo(() => ({
    favouriteEventTracker,
    source: favouriteSource ?? (isOnMapSidePanel ? PROPERTY_FAVOURITED_SOURCE_MAP_GRID : PROPERTY_FAVOURITED_SOURCE_AREA_PAGE),
  }), [favouriteEventTracker, favouriteSource, isOnMapSidePanel]);

  const openListingModal = (event: MouseEvent) => {
    if (onListingCardClick) {
      onListingCardClick({
        event,
        isNoModalLocation,
        addressPathWithFallback,
        listingId,
        showsListing,
        isPrivate,
        isSignInAndTermsOfUseRequired,
        isTermsRequired,
        providerId,
      });
    }
  };

  const onImageClick = (event: MouseEvent) => {
    event.preventDefault();
  };

  const cardImageClass = buildClassName(styles.image, showGatedBody && styles.blurImage);
  const listingCardClass = buildClassName(styles.component, 'listing-card', useExpStyle && styles.exp);

  useEffect(() => {
    if (listingCardElement.current) {
      setIsSmallCard(listingCardElement.current.offsetWidth < 280);
    }
  }, [windowWidth]);

  const trackNoModalEvents = () => {
    if (gtmOnClickEvent) {
      trackEvent(gtmOnClickEvent);
    }
    trackEvent('Xp_UiAddresspView');
  };

  const addressLabel = displayAddress
    ? generateFullAddress(street, city, province, shouldScramble)
    : t(LOCALE_COMMON_PATHS.LISTING_CARD_ADDRESS_NOT_AVAILABLE, { defaultValue: 'Address not available' });

  return (
    <ConditionalWrapper condition={noModal} wrapper={children => (
      <Link
        href={addressPathWithFallback}
        target={noModal ? '_blank' : ''}
        rel={!listing.displayAddress ? 'nofollow' : undefined}
        onClick={trackNoModalEvents}
      >
        {children}
      </Link>
    )}
    >
      <div
        className={listingCardClass}
        ref={listingCardElement}
        onClick={openListingModal}
        data-testid={listingCardIds.listingCard}
        {...{ 'dynamic-testid': String(listingId) }}
      >
        <div className={styles.media}>
          <AspectRatio ratio="16:11">
            <ConditionalWrapper
              condition={!noModal && isImageReady}
              wrapper={children => <Link href={addressPathWithFallback} onClick={onImageClick}>{children}</Link>}
            >
              <ListingImage
                item={{
                  image: getImageSrc(imageUrl),
                  alt: t(LOCALE_COMMON_PATHS.LISTING_CARD_MEDIA_ALT, { alt: imageAlt, defaultValue: `${imageAlt} | Card Image` }),
                  id: `${listing.id}-card-image`,
                  isImageReady,
                  addedAt: listing.addedAt,
                }}
                isVisible={isImageVisible}
                className={cardImageClass}
                testId={listingCardIds.listingImage}
                usePriorityMode={isCrawler}
                isListingCardImage={true}
              />
            </ConditionalWrapper>
            {showGatedBody && <div className={styles.mediaVeil} />}
            {showGatedBody && <GatedMediaPill isTerms={showTermsPill} />}
            {pvrSentDate && (
              <p className={styles.pvrDate}>
                {t(LOCALE_COMMON_PATHS.LISTING_CARD_PVR_DATE_LABEL, {
                  date: formatDateWithTemplate(pvrSentDate, 'MMM DD, YYYY'),
                  defaultValue: 'Request sent on {{date}}',
                })}
              </p>
            )}
            {!showGatedBody && (
              <div className={styles.statusChrome}>
                {/* Coming Soon is excluded here: ComingSoonBadge below owns that status and
                    renders a richer pill (calendar icon + go-live date). Both components sit in
                    this same overlay, so without this guard a Coming Soon listing shows two
                    identically-labelled pills stacked at the top left. */}
                {showsListing && standardStatus != null && !(isActive(standardStatus) && !listing.isRental) && !isComingSoon(standardStatus) && (
                  <ListingStatus
                    status={listing.standardStatus}
                    isRental={listing.isRental}
                    providerId={providerId}
                    mlsStatus={listing.mlsStatus}
                    size={LISTING_STATUS_SIZES.SMALL}
                    className={styles.statusPill}
                  />
                )}
                <ComingSoonBadge
                  standardStatus={standardStatus}
                  onMarketDate={listing.onMarketDate}
                  providerId={providerId}
                  position="top-left"
                  offset="0"
                />
                {matchedSmartTags.length > 0 && (
                  <div className={styles.smartTags} data-testid={listingCardIds.listingSmartTags}>
                    {matchedSmartTags.map(tag => (
                      <ListingBadge
                        key={tag}
                        variant="smart-tag"
                        label={capitalizeWords(tag)}
                        className={styles.smartTagBadge}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
            {showFavouriteToggle && (
              <div className={styles.favouriteChrome}>
                <FavouriteToggle
                  listingId={favouriteListingId}
                  addressSlug={favouriteAddressSlug}
                  analytics={analyticsProps}
                  variant="burst"
                />
              </div>
            )}
          </AspectRatio>
        </div>
        <div className={styles.wrapper}>
          {showGatedBody
            ? (
              <div className={styles.gatedBody}>
                <ListingCardNewOverlay
                  isLoginRequired={isHidden}
                  isTermsRequired={isTermsRequired}
                  isSmallCard={isSmallCard}
                  isCrea={isCrea}
                />
              </div>
            )
            : (
              <div className={styles.bottomContent}>
                <div>
                  <div className={styles.priceRow} data-testid={listingCardIds.listingCardPrice}>
                    <ListingPrice
                      listing={listing}
                      isVisible={showsListing}
                      isMinimal={true}
                      testId={listingCardIds.listingPrice}
                      shouldScramble={shouldScramble}
                    />
                    <ListingTimestamp
                      listing={listing}
                      isSmallCard={isSmallCard}
                      isMinimal={true}
                      className={styles.dateAdded}
                      testId={listingCardIds.listingTimeStamp}
                      isVisible={showsListing}
                      shouldScramble={shouldScramble}
                      isRental={listing.isRental}
                    />
                  </div>
                  <div className={styles.row}>
                    <SchemaOrg itemType="SingleFamilyResidence">
                      <SchemaOrg itemProp="address" itemType="PostalAddress">
                        {noModal
                          ? (
                            <div className={styles.streetAddress} data-testid={listingCardIds.listingAddress}>
                              <p>{addressLabel}</p>
                            </div>
                          )
                          : (
                            <p className={styles.streetAddress} data-testid={listingCardIds.listingAddress}>
                              <Link
                                href={listingId > 0 ? `${addressPathWithFallback}?listingId=${listingId}` : addressPathWithFallback}
                                target="_blank"
                              >
                                {addressLabel}
                              </Link>
                            </p>
                          )}
                        <meta itemProp="streetAddress" content={displayAddress && street ? street : ''} />
                        <meta itemProp="addressLocality" content={city || ''} />
                        <meta itemProp="addressRegion" content={province || ''} />
                      </SchemaOrg>
                      <SchemaOrg itemProp="geo" itemType="GeoCoordinates">
                        <meta itemProp="latitude" content={position ? position.latitude.toString() : ''} />
                        <meta itemProp="longitude" content={position ? position.longitude.toString() : ''} />
                      </SchemaOrg>
                    </SchemaOrg>
                  </div>
                  <ListingBedBathSqftLabels
                    listing={listing}
                    listingToCompare={listingToCompare}
                    obfuscate={isHidden || isTermsRequired}
                    tenantId={tenantId}
                    hideSquareFootage={shouldHideSquareFootage}
                  />
                </div>
                <div className={styles.providerMeta}>
                  <ProviderLabels
                    isBlurred={isHidden || isTermsRequired}
                    hasLogo={isUsStateCode(province?.toLowerCase() as ProvinceOrStateCode)}
                    brokerage={listing.brokerage}
                    mlsNum={listing.mlsNum}
                    providerId={listing.providerId}
                  />
                  <ProviderLogo listing={listing} isOnMapSidePanel={isOnMapSidePanel} />
                </div>
              </div>
            )}
        </div>
      </div>
    </ConditionalWrapper>
  );
}
